import { FC } from 'react'

import minusSvg from 'assets/minus.svg'
import plusSvg from 'assets/plus.svg'

import {
  AmountHandlerContainer,
  AmountHandlerButton,
  AmountHandlerCounter,
  DualAmountContainer,
  DualStepButton,
  DualAmountValue,
} from './styled'

type AmountHandlerType = {
  currentValue: number
  multiplier?: number
  step?: number
  // QRM-524: when set, renders the dual-step layout (-coarse -fine value +fine +coarse)
  coarseStep?: number
  fineLabel?: string
  coarseLabel?: string
  unit?: string
  onDecrease: (value: number) => void
  onIncrease: (value: number) => void
  minValue?: number
  maxValue?: number
  isSmall?: boolean
}

const getDecimalPlaces = (num: number): number => {
  if (Number.isInteger(num)) return 0
  const str = num.toString()
  if (str.includes('.')) {
    return str.split('.')[1].length
  }
  if (str.includes('e-')) {
    const match = str.match(/e-(\d+)/)
    if (match) {
      return parseInt(match[1], 10)
    }
  }
  return 0
}

export const AmountHandler: FC<AmountHandlerType> = ({
  currentValue,
  multiplier = 1,
  step = 1,
  coarseStep,
  fineLabel,
  coarseLabel,
  unit,
  minValue = 0,
  maxValue,
  onDecrease,
  onIncrease,
  ...props
}) => {
  const decimalPlaces = getDecimalPlaces(step)
  const displayValue = (currentValue * multiplier).toFixed(decimalPlaces)

  // QRM-524 dual-step mode. Count precision comes from the fine step; the change
  // is clamped into [minValue, maxValue] so a coarse press near a bound lands on
  // the bound instead of being ignored.
  const changeBy = (e: any, delta: number) => {
    e.stopPropagation()
    const raw = +(currentValue + delta).toFixed(decimalPlaces)
    const bounded = Math.min(maxValue || Infinity, Math.max(minValue, raw))
    if (bounded === currentValue) return
    if (delta < 0) onDecrease(bounded)
    else onIncrease(bounded)
  }

  if (coarseStep) {
    // display precision derives from the fine step in DISPLAY units (step×multiplier:
    // 10g → integers, 0.1kg → one decimal); trailing zeros are trimmed ("5", not "5.0")
    const displayStepDecimals = getDecimalPlaces(
      +(step * multiplier).toFixed(6)
    )
    const dualDisplayValue = String(
      +(currentValue * multiplier).toFixed(displayStepDecimals)
    )
    const decreaseDisabled = currentValue <= minValue
    const increaseDisabled =
      Boolean(maxValue) && currentValue >= (maxValue as number)
    return (
      <DualAmountContainer {...props}>
        <DualStepButton
          disabled={decreaseDisabled}
          onClick={(e: any) => changeBy(e, -coarseStep)}
          data-testid="amountHandeler_decreaseCoarseButton"
        >
          -{coarseLabel}
        </DualStepButton>
        <DualStepButton
          disabled={decreaseDisabled}
          onClick={(e: any) => changeBy(e, -step)}
          data-testid="amountHandeler_decreaseButton"
        >
          -{fineLabel}
        </DualStepButton>
        <DualAmountValue data-testid="amountHandeler_display">
          {dualDisplayValue}
          <span>{unit}</span>
        </DualAmountValue>
        <DualStepButton
          disabled={increaseDisabled}
          onClick={(e: any) => changeBy(e, step)}
          data-testid="amountHandeler_increaseButton"
        >
          +{fineLabel}
        </DualStepButton>
        <DualStepButton
          disabled={increaseDisabled}
          onClick={(e: any) => changeBy(e, coarseStep)}
          data-testid="amountHandeler_increaseCoarseButton"
        >
          +{coarseLabel}
        </DualStepButton>
      </DualAmountContainer>
    )
  }

  const handleDecrease = (e: any) => {
    e.stopPropagation()
    if ((currentValue || currentValue === 0) && currentValue > minValue) {
      onDecrease(
        +(currentValue - step).toFixed(
          step.toString().length > 2 ? step.toString().length - 2 : 0
        )
      )
    }
  }
  const handleIncrease = (e: any) => {
    e.stopPropagation()
    if (
      (currentValue || currentValue === 0) &&
      (!maxValue || currentValue < maxValue)
    ) {
      onIncrease(
        +(currentValue + step).toFixed(
          step.toString().length > 2 ? step.toString().length - 2 : 0
        )
      )
    }
  }
  return (
    <AmountHandlerContainer {...props}>
      <AmountHandlerButton
        disabled={currentValue <= minValue}
        onClick={handleDecrease}
        data-testid="amountHandeler_decreaseButton"
      >
        <img src={minusSvg} alt="-" />
      </AmountHandlerButton>
      <AmountHandlerCounter data-testid="amountHandeler_display">
        {displayValue}
      </AmountHandlerCounter>
      <AmountHandlerButton
        onClick={handleIncrease}
        data-testid="amountHandeler_increaseButton"
      >
        <img src={plusSvg} alt="+" />
      </AmountHandlerButton>
    </AmountHandlerContainer>
  )
}
